/**
 * Simple Token-Based Authentication
 *
 * A lightweight auth system using a single access token.
 * No external dependencies - just localStorage and React context.
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

// Access token - can be set via environment variable or hardcoded
const ACCESS_TOKEN = import.meta.env.VITE_ACCESS_TOKEN || 'ed-dua-2026';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_STORAGE_KEY = 'secondary-market-auth';

export function AuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    // Check localStorage on initial load
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      return stored === 'true';
    }
    return false;
  });

  useEffect(() => {
    // Sync auth state to localStorage
    localStorage.setItem(AUTH_STORAGE_KEY, String(isAuthenticated));
  }, [isAuthenticated]);

  const login = (token: string): boolean => {
    if (token === ACCESS_TOKEN) {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const logout = (): void => {
    setIsAuthenticated(false);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
